import { PrivacyMaskProvider } from "@nimit9/signet-ui/components/privacy-mask"
import { TooltipProvider } from "@nimit9/signet-ui/components/tooltip"
import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import React from "react"
import ReactDOM from "react-dom/client"
import { Toaster } from "react-hot-toast"
import { ErrorBoundary, RouteErrorComponent } from "./components/common/error-boundary"
import { PwaUpdatePrompt } from "./components/pwa-update-prompt"
import { ThemeProvider } from "./components/theme-provider"
import { startAnalytics } from "./lib/analytics"
import { api } from "./lib/api"
import { flushQueue } from "./lib/offline-queue"
import { queryClient } from "./lib/query-client"
import { cleanupSharePayloadOnStartup } from "./lib/share-target"
import { routeTree } from "./routeTree.gen"
import "./index.css"

async function syncOfflineQueue() {
  const synced = await flushQueue((tx) => api.post("/transactions", tx).then(() => undefined))
  if (synced > 0) {
    queryClient.invalidateQueries({ queryKey: ["transactions"] })
    queryClient.invalidateQueries({ queryKey: ["dashboard"] })
  }
}

window.addEventListener("online", () => void syncOfflineQueue())

// Purge a staged OS share (possibly a statement PDF) left past its TTL by a
// crash, reload or closed tab — it must not sit in Cache Storage for good.
void cleanupSharePayloadOnStartup()
void syncOfflineQueue()

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Only send 10% of performance traces; capture all replays on error
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
  // Only active in production; VITE_SENTRY_DSN is unset in dev
  enabled: !!(import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD),
})

const router = createRouter({ routeTree, defaultErrorComponent: RouteErrorComponent })

const trackScreen = startAnalytics()
trackScreen(window.location.pathname)
router.subscribe("onResolved", ({ pathChanged, toLocation }) => {
  if (pathChanged) trackScreen(toLocation.pathname)
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const root = document.getElementById("root")
if (!root) throw new Error("Root element not found")
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <TooltipProvider>
          {/* PAISA-PRIV-01: the header's "Hide amounts" mask — per-device,
              persisted under paisa's existing storage key so a saved
              preference survives this migration. */}
          <PrivacyMaskProvider storageKey="paisa-mask-amounts">
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              <PwaUpdatePrompt />
              <Toaster
                position="bottom-center"
                toastOptions={{
                  style: { background: "var(--bg-surface)", color: "var(--text-primary)" },
                }}
              />
            </QueryClientProvider>
          </PrivacyMaskProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
